import { resolveSupportedLocale } from './supportedLocales';

/**
 * City-specific visual storytelling config for TLA Housing Europe locations.
 *
 * Matching: normalize location name to lowercase, find the first key that the
 * name contains. This tolerates "Ramstein / Kaiserslautern", "Grafenwöhr", etc.
 *
 * Images: All Unsplash CDN URLs following the existing project pattern.
 * TODO: Verify each city image URL renders the intended landmark/scene.
 *       Replace any mismatched IDs with a verified city-specific photo.
 *
 * Batch D will add admin-editable media management; this file is the static
 * frontend fallback layer until that is implemented.
 */

const BASE_W_H = 'w=1200&h=600&fit=crop';
const CARD_W_H = 'w=600&h=400&fit=crop';
const UNSPLASH = 'https://images.unsplash.com';

// Verified generic European city images already used in this repo
const GENERIC_A = `${UNSPLASH}/photo-1467269204594-9661b134dd2b`; // European rooftop cityscape
const GENERIC_B = `${UNSPLASH}/photo-1599946347371-68eb71b16afc`; // Rhine-style river city
const GENERIC_C = `${UNSPLASH}/photo-1449452198679-05c7fd30f416`; // European town square

export const LOCATION_VISUALS = {
  wiesbaden: {
    // TODO: Verify — should show Wiesbaden Kurhaus, Wilhelmstrasse, or Rhine promenade
    heroImage:  `${UNSPLASH}/photo-1558618666-fcd25c85cd64?${BASE_W_H}`,
    cardImage:  `${UNSPLASH}/photo-1558618666-fcd25c85cd64?${CARD_W_H}`,
    tagline: 'Elegant Rhine-side living — minutes from Clay Kaserne',
    tagline_de: 'Elegantes Wohnen am Rhein — Minuten von Clay Kaserne',
    tagline_es: 'Vida elegante junto al Rin — a minutos de Clay Kaserne',
    lifestyleHighlights: [
      'Direct commute to Lucius D. Clay Kaserne',
      'Historic Kurhaus spa district and casino',
      'Rhine riverfront walks and wine culture',
      'Top-rated international and DoDDS schools',
      'Christmas markets and annual wine festival',
    ],
    lifestyleHighlights_de: [
      'Direkte Pendlerstrecke zur Lucius D. Clay Kaserne',
      'Historisches Kurhaus-Spa-Viertel und Kasino',
      'Rheinpromenade und Weinkultur',
      'Ausgezeichnete internationale und DoDDS-Schulen',
      'Weihnachtsmärkte und jährliches Weinfest',
    ],
    lifestyleHighlights_es: [
      'Acceso directo a Lucius D. Clay Kaserne',
      'Histórico distrito balneario Kurhaus y casino',
      'Paseos junto al Rin y cultura vitivinícola',
      'Excelentes escuelas internacionales y DoDDS',
      'Mercados navideños y festival anual del vino',
    ],
    localFlavor: ['Rhine riverfront', 'Kurhaus & spa', 'Wine country', 'DoDDS schools'],
    localFlavor_de: ['Rheinpromenade', 'Kurhaus & Spa', 'Weinland', 'DoDDS-Schulen'],
    localFlavor_es: ['Orilla del Rin', 'Kurhaus & spa', 'Región vinícola', 'Escuelas DoDDS'],
    fallback: `${GENERIC_B}?${CARD_W_H}`,
  },

  kaiserslautern: {
    // TODO: Verify — should show Kaiserslautern city center or Palatinate Forest
    heroImage:  `${UNSPLASH}/photo-1448375240586-882707db888b?${BASE_W_H}`,
    cardImage:  `${UNSPLASH}/photo-1448375240586-882707db888b?${CARD_W_H}`,
    tagline: 'Gateway to Palatinate Forest — the city built around Ramstein',
    tagline_de: 'Tor zum Pfälzerwald — die Stadt rund um Ramstein',
    tagline_es: 'Puerta al Bosque del Palatinado — la ciudad construida alrededor de Ramstein',
    lifestyleHighlights: [
      'Minutes from Ramstein Air Base and Spangdahlem',
      'Palatinate Forest for hiking, cycling, and skiing',
      'Large, established American military community',
      'Kaiserslautern American High School (KAHS)',
      'Affordable housing compared to Frankfurt or Stuttgart',
    ],
    lifestyleHighlights_de: [
      'Minuten von Ramstein Air Base und Spangdahlem',
      'Pfälzerwald zum Wandern, Radfahren und Skifahren',
      'Große, etablierte US-Militärgemeinschaft',
      'Kaiserslautern American High School (KAHS)',
      'Günstiger Wohnraum verglichen mit Frankfurt oder Stuttgart',
    ],
    lifestyleHighlights_es: [
      'A minutos de Ramstein Air Base y Spangdahlem',
      'Bosque del Palatinado para senderismo, ciclismo y esquí',
      'Gran comunidad militar estadounidense establecida',
      'Kaiserslautern American High School (KAHS)',
      'Vivienda asequible comparada con Fráncfort o Stuttgart',
    ],
    localFlavor: ['Ramstein proximity', 'Palatinate Forest', 'KAHS', 'Military community'],
    localFlavor_de: ['Ramstein-Nähe', 'Pfälzerwald', 'KAHS', 'Militärgemeinschaft'],
    localFlavor_es: ['Cercanía a Ramstein', 'Bosque del Palatinado', 'KAHS', 'Comunidad militar'],
    fallback: `${GENERIC_C}?${CARD_W_H}`,
  },

  stuttgart: {
    // TODO: Verify — should show Stuttgart panorama, wine terraces, or Mercedes/Porsche area
    heroImage:  `${UNSPLASH}/photo-1560179707-f14e90ef3623?${BASE_W_H}`,
    cardImage:  `${UNSPLASH}/photo-1560179707-f14e90ef3623?${CARD_W_H}`,
    tagline: 'Engineering capital with hillside vineyards and world-class culture',
    tagline_de: 'Ingenieurhauptstadt mit Weinbergen und Weltklasse-Kultur',
    tagline_es: 'Capital de la ingeniería con viñedos y cultura de clase mundial',
    lifestyleHighlights: [
      'Close to Patch Barracks, Panzer Kaserne, and USAG Stuttgart',
      'Porsche and Mercedes-Benz museums',
      'Wine terraces on city hillsides — unique in Germany',
      'Outstanding international school choices',
      'Direct trains to Munich, Frankfurt, and Heidelberg',
    ],
    lifestyleHighlights_de: [
      'In der Nähe von Patch Barracks, Panzer Kaserne und USAG Stuttgart',
      'Porsche- und Mercedes-Benz-Museen',
      'Weinberge auf Stadthügeln — einzigartig in Deutschland',
      'Ausgezeichnete internationale Schulen',
      'Direktzüge nach München, Frankfurt und Heidelberg',
    ],
    lifestyleHighlights_es: [
      'Cerca de Patch Barracks, Panzer Kaserne y USAG Stuttgart',
      'Museos Porsche y Mercedes-Benz',
      'Viñedos en las laderas de la ciudad — únicos en Alemania',
      'Excelentes opciones de escuelas internacionales',
      'Trenes directos a Múnich, Fráncfort y Heidelberg',
    ],
    localFlavor: ['Patch Barracks', 'Porsche & Mercedes', 'Wine hillsides', 'Arts & culture'],
    localFlavor_de: ['Patch Barracks', 'Porsche & Mercedes', 'Weinberge', 'Kunst & Kultur'],
    localFlavor_es: ['Patch Barracks', 'Porsche & Mercedes', 'Viñedos', 'Arte y cultura'],
    fallback: `${GENERIC_A}?${CARD_W_H}`,
  },

  grafenwoehr: {
    // TODO: Verify — should show Bavarian countryside, forest, or Grafenwöhr village
    heroImage:  `${UNSPLASH}/photo-1504701954957-2010ec3bcec1?${BASE_W_H}`,
    cardImage:  `${UNSPLASH}/photo-1504701954957-2010ec3bcec1?${CARD_W_H}`,
    tagline: 'Authentic Bavaria — on-post life meets Bavarian village charm',
    tagline_de: 'Authentisches Bayern — On-Post-Leben trifft bayerischen Dorfcharme',
    tagline_es: 'Baviera auténtica — vida en la base con encanto de aldea bávara',
    lifestyleHighlights: [
      'Walking distance to Rose Barracks and training area',
      'Genuine Bavarian beer gardens, festivals, and cuisine',
      'Easy access to Bavarian Alps for skiing and hiking',
      'Close-knit, tight military family community',
      'Lower cost of living than major German cities',
    ],
    lifestyleHighlights_de: [
      'Fußläufig zu Rose Barracks und dem Truppenübungsplatz',
      'Echte bayerische Biergärten, Feste und Küche',
      'Einfacher Zugang zu den Bayerischen Alpen',
      'Enge, vertraute Militärfamiliengemeinschaft',
      'Günstigere Lebenshaltungskosten als in deutschen Großstädten',
    ],
    lifestyleHighlights_es: [
      'A poca distancia de Rose Barracks y el área de entrenamiento',
      'Auténticos jardines de cerveza bávaros, festivales y gastronomía',
      'Fácil acceso a los Alpes bávaros para esquiar y senderismo',
      'Comunidad militar familiar muy unida',
      'Coste de vida más bajo que en las grandes ciudades alemanas',
    ],
    localFlavor: ['Rose Barracks', 'Bavaria & Alps', 'Beer gardens', 'Outdoor recreation'],
    localFlavor_de: ['Rose Barracks', 'Bayern & Alpen', 'Biergärten', 'Outdoor'],
    localFlavor_es: ['Rose Barracks', 'Baviera y Alpes', 'Jardines de cerveza', 'Naturaleza'],
    fallback: `${GENERIC_C}?${CARD_W_H}`,
  },

  vilseck: {
    // Vilseck shares Grafenwoehr visuals (same training area / same region)
    // TODO: Verify or differentiate from Grafenwoehr if separate config is needed
    heroImage:  `${UNSPLASH}/photo-1504701954957-2010ec3bcec1?${BASE_W_H}`,
    cardImage:  `${UNSPLASH}/photo-1504701954957-2010ec3bcec1?${CARD_W_H}`,
    tagline: 'Rose Barracks country — Bavaria at your doorstep',
    tagline_de: 'Rose Barracks Land — Bayern direkt vor der Tür',
    tagline_es: 'Tierra de Rose Barracks — Baviera a tu puerta',
    lifestyleHighlights: [
      'Home of Rose Barracks (2nd Cavalry Regiment)',
      'Bavarian village atmosphere with on-post access',
      'Grafenwöhr Training Area minutes away',
      'Alpine recreation within weekend driving distance',
      'Strong military family support network',
    ],
    lifestyleHighlights_de: [
      'Heimat der Rose Barracks (2. Kavallerieregiment)',
      'Bayerisches Dorfflair mit On-Post-Zugang',
      'Truppenübungsplatz Grafenwöhr in der Nähe',
      'Alpenurlaub am Wochenende in Reichweite',
      'Starkes militärisches Familiennetzwerk',
    ],
    lifestyleHighlights_es: [
      'Sede de Rose Barracks (2.º Regimiento de Caballería)',
      'Ambiente de aldea bávara con acceso a la base',
      'Área de entrenamiento de Grafenwoehr a minutos',
      'Recreación alpina al alcance de un fin de semana',
      'Sólida red de apoyo para familias militares',
    ],
    localFlavor: ['Rose Barracks', 'Bavaria', 'Training area', 'Alpine recreation'],
    localFlavor_de: ['Rose Barracks', 'Bayern', 'Truppenübungsplatz', 'Alpin'],
    localFlavor_es: ['Rose Barracks', 'Baviera', 'Área de entrenamiento', 'Recreación alpina'],
    fallback: `${GENERIC_C}?${CARD_W_H}`,
  },

  heidelberg: {
    // TODO: Verify — should show Heidelberg castle, Old Bridge, or Neckar Valley
    heroImage:  `${UNSPLASH}/photo-1578662996442-48f60103fc96?${BASE_W_H}`,
    cardImage:  `${UNSPLASH}/photo-1578662996442-48f60103fc96?${CARD_W_H}`,
    tagline: 'Romantic castle city on the Neckar — a gem for relocation families',
    tagline_de: 'Romantische Schlossstadt am Neckar — ein Juwel für Umzugsfamilien',
    tagline_es: 'Ciudad del castillo romántico en el Neckar — una joya para familias en reubicación',
    lifestyleHighlights: [
      'UNESCO-worthy castle, Old Town, and Philosophers\' Walk',
      'Neckar River cycling paths and boat tours',
      'University city energy with vibrant café and arts scene',
      'Direct trains to Mannheim, Frankfurt, and Stuttgart',
      'Consistently top-rated for expat quality of life',
    ],
    lifestyleHighlights_de: [
      'UNESCO-würdiges Schloss, Altstadt und Philosophenweg',
      'Radwege und Bootstouren am Neckar',
      'Universitätsstadtatmosphäre mit lebhafter Café- und Kunstszene',
      'Direktzüge nach Mannheim, Frankfurt und Stuttgart',
      'Regelmäßig top bewertet für Expat-Lebensqualität',
    ],
    lifestyleHighlights_es: [
      'Castillo digno de la UNESCO, Casco Antiguo y Sendero de los Filósofos',
      'Rutas ciclistas y excursiones en barco por el Neckar',
      'Energía universitaria con vibrante escena de cafés y arte',
      'Trenes directos a Mannheim, Fráncfort y Stuttgart',
      'Constantemente top en calidad de vida para expatriados',
    ],
    localFlavor: ['Heidelberg Castle', 'Old Bridge', 'Neckar River', 'University city'],
    localFlavor_de: ['Heidelberger Schloss', 'Alte Brücke', 'Neckar', 'Universitätsstadt'],
    localFlavor_es: ['Castillo de Heidelberg', 'Puente Viejo', 'Río Neckar', 'Ciudad universitaria'],
    fallback: `${GENERIC_B}?${CARD_W_H}`,
  },

  frankfurt: {
    // TODO: Verify — should show Frankfurt Römerberg, skyline, or Sachsenhausen
    heroImage:  `${UNSPLASH}/photo-1541888946425-d81bb19240f5?${BASE_W_H}`,
    cardImage:  `${UNSPLASH}/photo-1541888946425-d81bb19240f5?${CARD_W_H}`,
    tagline: 'Europe\'s financial hub — surprisingly livable for relocation families',
    tagline_de: 'Europas Finanzzentrum — überraschend familienfreundlich',
    tagline_es: 'El centro financiero de Europa — sorprendentemente habitable para familias',
    lifestyleHighlights: [
      'Europe\'s most connected airport — ideal for frequent travel',
      'Large international school ecosystem',
      'Sachsenhausen for apple wine, culture, and nightlife',
      'Short train to Rhine Valley, Heidelberg, and wine country',
      'Multicultural dining, arts, and expat community',
    ],
    lifestyleHighlights_de: [
      'Europas bestvernetzter Flughafen — ideal für Vielreisende',
      'Großes internationales Schulangebot',
      'Sachsenhausen für Apfelwein, Kultur und Nachtleben',
      'Kurze Bahnfahrt ins Rheintal, nach Heidelberg und ins Weinland',
      'Multikulturelles Essen, Kunst und Expat-Gemeinschaft',
    ],
    lifestyleHighlights_es: [
      'El aeropuerto mejor conectado de Europa — ideal para viajeros frecuentes',
      'Gran ecosistema de escuelas internacionales',
      'Sachsenhausen para vino de manzana, cultura y vida nocturna',
      'Tren corto al Valle del Rin, Heidelberg y la región vinícola',
      'Gastronomía multicultural, arte y comunidad de expatriados',
    ],
    localFlavor: ['FRA Airport', 'Römer & Old Town', 'International schools', 'Expat hub'],
    localFlavor_de: ['FRA-Flughafen', 'Römer & Altstadt', 'Internationale Schulen', 'Expat-Hub'],
    localFlavor_es: ['Aeropuerto FRA', 'Römer y Casco Antiguo', 'Escuelas internacionales', 'Hub expatriado'],
    fallback: `${GENERIC_A}?${CARD_W_H}`,
  },
};

/** Generic fallback used when no city key matches */
export const FALLBACK_VISUALS = {
  heroImage:  `${GENERIC_A}?${BASE_W_H}`,
  cardImage:  `${GENERIC_A}?${CARD_W_H}`,
  tagline: 'Relocation-ready housing in Germany and Europe',
  tagline_de: 'Umzugsbereites Wohnen in Deutschland und Europa',
  tagline_es: 'Alojamiento listo para la reubicación en Alemania y Europa',
  lifestyleHighlights: [],
  lifestyleHighlights_de: [],
  lifestyleHighlights_es: [],
  localFlavor: [],
  localFlavor_de: [],
  localFlavor_es: [],
  fallback: `${GENERIC_A}?${CARD_W_H}`,
};

/**
 * Look up visuals for a location by its display name.
 * Returns the matching config with locale-resolved tagline/highlights/flavor.
 */
export function getLocationVisuals(locationName, locale = 'en') {
  if (!locationName) return _resolveLocale(FALLBACK_VISUALS, locale);
  const lower = locationName.toLowerCase();
  const key = Object.keys(LOCATION_VISUALS).find((k) => lower.includes(k));
  return _resolveLocale(key ? LOCATION_VISUALS[key] : FALLBACK_VISUALS, locale);
}

function _resolveLocale(visuals, locale) {
  const loc = resolveSupportedLocale(locale);
  const suffix = loc === 'de' || loc === 'es' ? `_${loc}` : '';
  if (!suffix) return visuals;
  return {
    ...visuals,
    tagline: visuals[`tagline${suffix}`] || visuals.tagline,
    lifestyleHighlights: visuals[`lifestyleHighlights${suffix}`]?.length
      ? visuals[`lifestyleHighlights${suffix}`]
      : visuals.lifestyleHighlights,
    localFlavor: visuals[`localFlavor${suffix}`]?.length
      ? visuals[`localFlavor${suffix}`]
      : visuals.localFlavor,
  };
}
